
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { UserRole } from '@/types/feedback';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  adminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  adminOnly = false
}) => {
  const { user, profile, isLoading } = useAuth();
  const location = useLocation();

  // Afficher un loader pendant le chargement
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
    </div>;
  }

  // Rediriger vers la page d'authentification si l'utilisateur n'est pas connecté
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Si adminOnly est true, vérifier que l'utilisateur est admin
  if (adminOnly && profile && profile.role !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Si un rôle spécifique est requis, vérifier que l'utilisateur a ce rôle
  if (requiredRole && profile) {
    // Les administrateurs ont accès à toutes les routes
    if (profile.role === 'admin') {
      return <>{children}</>;
    }
    
    const hasRequiredRole = Array.isArray(requiredRole) 
      ? requiredRole.includes(profile.role as UserRole)
      : profile.role === requiredRole;
    
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
